import React, { useEffect, useState } from 'react';
import { TableRow, TableCell } from "@mui/material";
import Tooltip, { tooltipClasses, TooltipProps } from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import { Icon } from "@iconify/react";
import { USBPenDataInquiryStatus } from '../USBPenInfo';

import i18next from 'i18next';
import { INcodeRange, IOfflineData, IUSBConnectedPen } from '@/neolab-libs/nl-lib3-common/interfaces/IUSBConnectedPen';
import { IProjectSubmissionTransfer } from '@/repositories/model/transfer/IProjectSubmissionTransfer';
import { INcodeAllocationResponse } from '@/repositories/model/transfer/INcodeAllocationResponse';
import { USBAssignedUserMapper } from '@/common/objects/USBAssignedUserMapping/USBAssignedUserMapper';
import { IPageSOBP, SimpleLock } from '@/neolab-libs/nl-lib3-common';
import { NativeMethods } from '@/neolab-libs/nl-lib3-pengateway/src/nl-lib-native-methods/NativeMethods';
import { usbDeviceEventBus } from '../USBEventTypes';
import { PenStudentInfo } from '../../Bluetooth/PenStudentInfo';

type Props = {
  device: IUSBConnectedPen;
  projectSubmissionList: IProjectSubmissionTransfer[];
  ncodeAllocation: INcodeAllocationResponse;
  usbAssignedUserMapper: USBAssignedUserMapper;
  projectCode: string;
  studentInfos: PenStudentInfo[];
  onReadyChange?: (mac: string, ready: boolean) => void;
  onDuplicateChange?: (mac: string, isDuplicate: boolean) => void;
};

export function USBBatchUploadRow(props: Props) {
  const { device, projectSubmissionList, ncodeAllocation } = props;

  const [penInfoQueryStatus, setPenInfoQueryStatus] =
      useState<USBPenDataInquiryStatus>(USBPenDataInquiryStatus.idle);
  
  const [ncodeRange, setNcodeRange] = useState<INcodeRange | null>(null);
  const [offlineData, setOfflineData] = useState<IOfflineData | null>(null);
  const [offlineFileNames, setOfflineFileNames] = useState<string[] | null>(null);

  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [selectedStudentName, setSelectedStudentName] = useState<string | null>(null);

  // Ensure getOfflineData executes only once per row
  const hasTriggeredFetchRef = React.useRef<boolean>(false);

  // Keep latest values in refs to avoid stale-closure in event handlers
  const selectedStudentIdRef = React.useRef<string | null>(null);
  useEffect(() => { selectedStudentIdRef.current = selectedStudentId; }, [selectedStudentId]);

  const ncodeRangeRef = React.useRef<INcodeRange | null>(null);
  useEffect(() => { ncodeRangeRef.current = ncodeRange; }, [ncodeRange]);

  const offlineDataRef = React.useRef<IOfflineData | null>(null);
  useEffect(() => { offlineDataRef.current = offlineData; }, [offlineData]);

  const offlineFileNamesRef = React.useRef<string[] | null>(null);
  useEffect(() => { offlineFileNamesRef.current = offlineFileNames; }, [offlineFileNames]);

  const projectSubmissionListRef = React.useRef<IProjectSubmissionTransfer[] | null>(null);
  useEffect(() => { projectSubmissionListRef.current = projectSubmissionList; }, [projectSubmissionList]);

  // Keep duplicate status in a ref for event handlers
  const isDuplicateSelectionRef = React.useRef<boolean>(false);

  const lock = React.useRef<SimpleLock>(new SimpleLock());

  useEffect(() => {
      if (ncodeAllocation) {
        const { section } = ncodeAllocation;
        const start = {
          section,
          owner: ncodeAllocation.startOwner,
          book: ncodeAllocation.startBook,
          page: ncodeAllocation.startPage,
        };
        const end = {
          section,
          owner: ncodeAllocation.endOwner,
          book: ncodeAllocation.endBook,
          page: ncodeAllocation.endPage,
        };
        setNcodeRange({ start, end });
      }
    }, [ncodeAllocation]);

    // Auto-trigger getOfflineData when prerequisites are ready (only once)
    useEffect(() => {
      if (!hasTriggeredFetchRef.current && device?.mac && ncodeRange) { 
        getOfflineData(device.mac, ncodeRange);
      }
    }, [device.mac, ncodeRange]);

    useEffect(() => {
      // Respond to batch item requests from dialog (use refs for freshest values)
      const unsubscribeBatchRequest = usbDeviceEventBus.on('usb-request-batch-items', () => {
        try {
          if (isDuplicateSelectionRef.current) {
            usbDeviceEventBus.emit('usb-report-batch-item', { mac: device.mac, item: null });
            return;
          }
          const selectedId = selectedStudentIdRef.current;
          const range = ncodeRangeRef.current;
          const off = offlineDataRef.current;
          if (!selectedId || !range || !(off?.strokes?.length)) {
            usbDeviceEventBus.emit('usb-report-batch-item', { mac: device.mac, item: null });
            return;
          }
          const list = projectSubmissionListRef.current ?? [];
          const matchedSubmission = list.find((s) => s.userCode === selectedId);
          const item = {
            userCode: selectedId,
            range,
            strokes: off.strokes,
            submissionCode: matchedSubmission?.submissionCode,
          };
          usbDeviceEventBus.emit('usb-report-batch-item', { mac: device.mac, item });
        } catch (_e) {
          usbDeviceEventBus.emit('usb-report-batch-item', { mac: device.mac, item: null });
        }
      });

    const unsubscribeDeleteRequest = (usbDeviceEventBus as any).on('usb-request-delete-files', async (payload?: { macs?: string[] }) => {
        try {
          const targets = payload?.macs;
          if (Array.isArray(targets) && targets.length > 0) {
            if (!targets.includes(device.mac)) return;
          }
          deleteFiles();
        } catch (_e) {
          // ignore delete errors
        }
      });

      // Respond with currently selected student id for duplicate detection
      const unsubscribeSelectedRequest = (usbDeviceEventBus as any).on('usb-request-selected-students', () => {
        const selectedId = selectedStudentIdRef.current;
        (usbDeviceEventBus as any).emit('usb-report-selected-student', { mac: device.mac, studentId: selectedId });
      });

      // Track others' selections in this row to detect duplicates locally
      const unsubscribeReportSelected = (usbDeviceEventBus as any).on(
        'usb-report-selected-student',
        (payload: { mac: string; studentId: string | null }) => {
          try {
            if (!payload || !payload.mac) return;
            setAllSelections((prev) => {
              const next = new Map(prev);
              next.set(payload.mac, payload.studentId ?? null);
              return next;
            });
          } catch (_e) {}
        }
      );
  
      return () => {
          unsubscribeBatchRequest();
          unsubscribeDeleteRequest();
          unsubscribeSelectedRequest();
          unsubscribeReportSelected();
      };
  }, [device.mac]);

  // Publish selection changes proactively
  useEffect(() => {
    (usbDeviceEventBus as any).emit('usb-report-selected-student', { mac: device.mac, studentId: selectedStudentId });
  }, [device.mac, selectedStudentId]);

  // On unmount, clear our selection so other rows can drop stale entries
  useEffect(() => {
    return () => {
      (usbDeviceEventBus as any).emit('usb-report-selected-student', { mac: device.mac, studentId: null });
      // Also notify dialog that any duplicate state for this row should be cleared
      props.onDuplicateChange && props.onDuplicateChange(device.mac, false);
    };
  }, [device.mac]);

  // Maintain a local view of all devices' selected student
  const [allSelections, setAllSelections] = useState<Map<string, string | null>>(new Map());

  // On mount, request current selected students to build initial map
  useEffect(() => {
    (usbDeviceEventBus as any).emit('usb-request-selected-students', {});
  }, []);

  // Always include our own current selection in the local map
  useEffect(() => {
    setAllSelections((prev) => {
      const next = new Map(prev);
      next.set(device.mac, selectedStudentId);
      return next;
    });
  }, [device.mac, selectedStudentId]);

  const isDuplicateSelection = React.useMemo(() => {
    if (!selectedStudentId) return false;
    let count = 0;
    for (const id of allSelections.values()) {
      if (id && id === selectedStudentId) count += 1;
      if (count > 1) return true;
    }
    return false;
  }, [allSelections, selectedStudentId]);

  useEffect(() => {
    isDuplicateSelectionRef.current = isDuplicateSelection;
    const duplicated = isDuplicateSelection;
    const hasSelection = !!selectedStudentId;
    const hasData = !!offlineData?.strokes?.length;
    const ready = !duplicated && hasSelection && hasData;
    if (device.mac) {
      props.onReadyChange && props.onReadyChange(device.mac, ready);
      props.onDuplicateChange && props.onDuplicateChange(device.mac, duplicated);
    }
  }, [isDuplicateSelection, selectedStudentId, offlineData, device.mac]);

    
  /**
     * offline data를 받아온다
     */
    const getOfflineData = async (
      deviceId: string,
      range: { start: IPageSOBP; end: IPageSOBP }
    ) => {
      if (!range) return;
      if (hasTriggeredFetchRef.current) return;
      hasTriggeredFetchRef.current = true;
      await lock.current.acquire();
  
  
      try {
            console.log("device: %s started", device.mac);
            setPenInfoQueryStatus(USBPenDataInquiryStatus.readingOfflineFile);
      
            const result = await NativeMethods.ReadOfflineStrokes(deviceId, range, USBAssignedUserMapper.assignedSOBP);
      
            const blocks = result[0] ?? [];
            const studentCodeBlocks = result[1] ?? [];
            const fileNames = result[2] ?? [];
      
            const strokes =
              (blocks ?? []).flatMap((b) => b.Strokes ?? b.strokes ?? []);
      
            // Use mapper.hitTest to determine selected student automatically
            let isMatched = false;
              try {
                const studentCodeStrokes = (studentCodeBlocks ?? []).flatMap((b) => b.Strokes ?? b.strokes ?? []);
                const mapper = props.usbAssignedUserMapper;
                const userCode = await mapper.hitTest(studentCodeStrokes, props.projectCode);
                if (userCode) {
                  const matched = (props.studentInfos || []).find(info => info.studentId === userCode);
                  if (matched) {
                    setSelectedStudentId(matched.studentId);

                    // TODO: - more infos for name value
                    const stringValue = matched.name + " (" + matched.grade + "/" + matched.className + "/" + matched.number + ")";
                    setSelectedStudentName(stringValue);
//                    props.handleStudentInfoSelected(props.device, matched);

                    // if offline data exists, notify the parent
                    isMatched = true;
                  }
                }
              } catch (e) {
                // ignore mapping errors
              }


            if (strokes.length > 0) {
              const pageKey = (s: IPageSOBP) =>
                `${s.section}:${s.owner}:${s.book}:${s.page}`;
              const numPages = new Set(
                blocks
                  .filter((b) => (b.Strokes ?? b.strokes ?? []).length > 0)
                  .map((b) => pageKey((b.sobp ?? b.Sobp)!))
              ).size;
      
              const offlineData: IOfflineData = {
                numPages,
                numStrokes: strokes.length,
                strokes,
              };
      
              // readiness will be reported via effect based on duplicate/selection/data
              
              
      
               setPenInfoQueryStatus(USBPenDataInquiryStatus.dataInRange);
              setOfflineData(offlineData);
              setOfflineFileNames(fileNames);
              
              } else {
              console.log("device: %s has no strokes", device.mac);
              setPenInfoQueryStatus(USBPenDataInquiryStatus.dataNotInRange);
              setOfflineData(null);
                setOfflineFileNames(null);
            }
          } catch (e) {
            console.log(e);
            setPenInfoQueryStatus(USBPenDataInquiryStatus.error);
            setOfflineData(null);
              setOfflineFileNames(null);
          } finally {
            lock.current.release();
          }
    };

    const deleteFiles = async () => {
        const selectedId = selectedStudentIdRef.current;
        const fileNames = offlineFileNamesRef.current;
        const off = offlineDataRef.current;
        if (selectedId && fileNames && fileNames.length > 0 && off?.strokes && off.strokes.length > 0) {
          NativeMethods.deleteFile(device.mac, fileNames);
        }
    }

  const isNoDataRow = (() => {
    const value = penInfoQueryStatus !== (USBPenDataInquiryStatus.dataInRange || USBPenDataInquiryStatus.readingOfflineFile);
    return value;
  })();

  return (
    <TableRow className={isNoDataRow ? "no-data" : undefined}>
      <TableCell align="center">{device?.mac || device?.name || '-'}</TableCell>
      <TableCell align="center">
        {selectedStudentName}
        {isDuplicateSelection && (
          <span style={{ marginLeft: 8, color: '#d32f2f' }}>
            {i18next.t("학생 중복 선택")}
          </span>
        )}
      </TableCell>
      <TableCell align="center">
        <DataStatusString status={penInfoQueryStatus} isDuplicate={isDuplicateSelection}/>
      </TableCell>
    </TableRow>
  );
}

// Data Status Chip Component
const CustomTooltip = styled((props: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: props.className }} />
))(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#FFFFFF",
    border: "2px solid #ff0000",
    color: "#000",
    fontSize: "14px",
    borderRadius: "16px",
    padding: "12px 16px",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#ff0000",
  },
}));

function DataStatusString({ status, isDuplicate }: { status: USBPenDataInquiryStatus, isDuplicate: boolean }) {
  // Match template styling: plain text for dataInRange, text + warning icon with tooltip for others
  if (status === USBPenDataInquiryStatus.dataInRange) {
    return (
      <span>
        <span>{i18next.t("데이터 있음")}</span>
        {isDuplicate && (
          <CustomTooltip
            title={
              <span>
                {i18next.t("동일한 학생이 중복으로 선택되었습니다. 펜 입력 내용을 개별적으로 확인 후, 다시 학생을 선택해주세요.")}
              </span>
            }
            arrow
          >
            {/* Absolutely position so it doesn't shift the centered label */}
            <span style={{ position: 'absolute', right: -35, top: '50%', transform: 'translateY(-50%)', display: 'inline-flex', alignItems: 'center', color: '#ff0000' }}>
              <Icon icon="ep:warning-filled" width={24} height={24} color="#ff0000" />
            </span>
          </CustomTooltip>
        )}
      </span>
    );
  }

  if (status === USBPenDataInquiryStatus.readingOfflineFile) {
    return (
      <span>
        {i18next.t("로딩중")}
      </span>
    );
  }

  if (status === USBPenDataInquiryStatus.dataNotInRange) {
    return (
      <span>
        {i18next.t("데이터 없음")}        
      </span>
    );
  }

  if (status === USBPenDataInquiryStatus.error) {
    return (
      <span>
        {i18next.t("데이터 없음")}
        <CustomTooltip
          title={
            <span>
              {i18next.t("오류 해결을 위해")} <span style={{ color: "#ff0000" }}>{i18next.t("펜을 크래들에서 분리 후")}</span> {i18next.t("다시 연결해주세요.")}
            </span>
          }
          arrow
        >
          {/* Absolutely position so it doesn't shift the centered label */}
            <span>
              <Icon icon="ep:warning-filled" width={24} height={24} color="#ff0000" />
            </span>
        </CustomTooltip>
      </span>
    );
  }

  return <span>-</span>;
}